import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import Button from 'app/components/base/Button'
import Dialog from 'app/components/base/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import SelectField from 'app/components/fields/SelectField'
import ErrorHandler, { isErrorHandlerType } from 'app/services/ErrorHandler'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { ChangeAddressSchema } from 'app/validation/schemas/listing/listing.schemas'

interface ChangeAddressFV {
  saved_address: string | number
}

interface ChangeAddressDialogProps {
  open: boolean
  options: { id?: string | number; address: string }[]
  onClose: () => void
  calculateShippingCost: (address: Address) => Promise<void>
}

const ChangeAddressDialog: RFC<ChangeAddressDialogProps> = ({
  open,
  options,
  onClose,
  calculateShippingCost
}) => {
  const methods = useForm<ChangeAddressFV>({
    resolver: yupResolver(ChangeAddressSchema),
    defaultValues: {
      saved_address: options[0]?.id
    }
  })

  const { isSubmitting } = methods.formState

  const onSubmit = async (values: ChangeAddressFV) => {
    try {
      await calculateShippingCost({
        id: String(values.saved_address),
        address: '',
        apartment: '',
        city: '',
        country: '',
        state: '',
        zip_code: '',
        full_address: '',
        formatted_address: ''
      })

      onClose()
    } catch (error) {
      console.error(error)

      if (isErrorHandlerType(error)) {
        new ErrorHandler(error?.data)
      }
    }
  }

  return (
    <Dialog
      open={open}
      titleSx={{ alignSelf: 'center' }}
      title={<FormattedMessage defaultMessage="Change Address" />}
      dialogSx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '527px',
            py: 5,
            px: { xs: 2, sm: 8 },
            borderRadius: 2
          },
          '.MuiDialogContent-root': {
            px: { xs: 0, sm: 3 }
          }
        }
      }}
      onClose={onClose}
      actions={
        <Button
          size="small"
          onClick={methods.handleSubmit(onSubmit)}
          loading={isSubmitting}
          sx={{
            mx: { xs: 0, sm: !IS_NATIVE_APP ? 3 : 0 },
            ...(IS_NATIVE_APP && { flex: 1 })
          }}
        >
          <FormattedMessage defaultMessage="Submit" />
        </Button>
      }
    >
      <FormProvider {...methods}>
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Text sx={{ textAlign: 'center' }} color="text.third">
              <FormattedMessage defaultMessage="You can select another address and we’ll calculate shipping cost for it." />
            </Text>
          </Stack>

          <SelectField
            name="saved_address"
            optionSettings={{
              key: 'saved_address',
              options: options,
              optionValue: 'id',
              optionLabel: 'address'
            }}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default ChangeAddressDialog
